import React from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { planAllows } from '../utils/planRule';

// FIX-3 / decision 9: the plan ladder and the rule live in utils/planRule.js, the one copy (the
// server's lib/planAccess.planAllows says the same). Re-exported here for the existing importers.
export { PLAN_RANK } from '../utils/planRule';
const REQUIRED_LABEL = { pro: 'Pro', team: 'Team Pro' };

/**
 * Whether the current user's plan includes a feature of the required tier (utils/planRule).
 */
export function userMeetsPlan(currentUser, requiredPlan) {
  return planAllows(currentUser, requiredPlan);
}

/**
 * Wraps a feature and shows a locked upgrade prompt when the user's plan
 * doesn't meet the requirement.
 *
 * Props:
 *   requiredPlan   'pro' | 'team'
 *   featureName    Display name of the feature
 *   featureDescription  Custom one-liner shown under the name
 *   setFilter      Optional: navigate to Pricing page
 *   children       Content shown when access is granted
 */
export default function PlanGate({
  requiredPlan = 'pro',
  featureName,
  featureDescription,
  setFilter,
  onUpgrade,
  children,
}) {
  const { currentUser } = useCurrentUser();

  if (userMeetsPlan(currentUser, requiredPlan)) {
    return children;
  }

  const planLabel = REQUIRED_LABEL[requiredPlan] || requiredPlan;
  const name = featureName || 'This feature';
  // PREP-3b: a covered business member can't self-upgrade (billing is owner-only).
  // If they hit this gate the business plan itself is below the requirement, so
  // point them at the account owner instead of a self-checkout CTA.
  const isCoveredMember = !!currentUser?.isCoveredMember;
  const desc = isCoveredMember
    ? `${name} needs the ${planLabel} plan. Contact your account owner to upgrade your business plan.`
    : (featureDescription || `Upgrade to ${planLabel} to unlock ${name.toLowerCase()}.`);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 2rem',
        textAlign: 'center',
        fontFamily: "'Raleway', sans-serif",
        minHeight: '320px',
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: '#f1f2f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.25rem',
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0C2D48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>

      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 800, color: '#0C2D48' }}>
        {name}
      </h3>
      <p style={{ margin: '0 0 1.75rem', color: '#666', fontSize: '0.9rem', maxWidth: '380px', lineHeight: 1.6 }}>
        {desc}
      </p>

      {isCoveredMember ? (
        <div style={{ padding: '0.7rem 1.5rem', background: '#f1f2f4', color: '#556', borderRadius: '10px', fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: '0.85rem' }}>
          👤 Managed by your account owner
        </div>
      ) : (
        <button
          onClick={() => {
            if (onUpgrade) {
              onUpgrade(requiredPlan);
              return;
            }
            if (setFilter) setFilter('Settings');
          }}
          style={{
            padding: '0.7rem 2rem',
            background: '#0C2D48',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
          onMouseOver={e => (e.currentTarget.style.opacity = '0.82')}
          onMouseOut={e => (e.currentTarget.style.opacity = '1')}
        >
          Upgrade to {planLabel}
        </button>
      )}
    </div>
  );
}
